import { stepMs } from './constants';
import type { Speed } from './types';

/**
 * Receptive fingerspelling drill: the avatar spells a word, the learner types
 * what they read. Fingerspelling is where drilling pays — names and places are
 * exactly what gets fingerspelled in real Auslan conversation, and reading a
 * hand at speed is the skill learners report struggling with.
 *
 * Everything here is pure so the machine is testable without a browser.
 */

/**
 * Words to drill: Australian places, common names, and everyday words a
 * learner will actually meet fingerspelled. Uppercase, A–Z only.
 */
export const WORD_POOL: string[] = [
  // Places
  'SYDNEY', 'PERTH', 'HOBART', 'DARWIN', 'CAIRNS', 'BONDI', 'ULURU', 'MANLY',
  'DUBBO', 'COBAR', 'YASS', 'EDEN', 'COOMA', 'ORANGE', 'BEGA', 'HAY',
  // Names
  'JACK', 'RUBY', 'OLIVE', 'HENRY', 'ALICE', 'OSCAR', 'EVIE', 'THEO',
  'MAEVE', 'BODHI', 'CLEO', 'HUGO', 'IVY', 'JUNE', 'LEO', 'MAX',
  'NINA', 'OWEN', 'PIPPA', 'QUINN', 'ROSE', 'SAM', 'TESS', 'UNA',
  'VERA', 'WREN', 'XAVIER', 'YURI', 'ZARA',
  // Everyday
  'EMU', 'KOALA', 'DINGO', 'GALAH', 'JOEY', 'BILBY', 'QUOKKA', 'WOMBAT',
  'TEA', 'PIE', 'BUS', 'TRAIN', 'FERRY', 'BEACH', 'RAIN', 'SURF',
  'FILM', 'BOOK', 'GAME', 'LUNCH', 'PARK', 'POOL', 'SHOP', 'VET',
  'QUIZ', 'JAZZ', 'KAYAK', 'MANGO', 'PAV', 'UTE', 'ARVO', 'BIKKIE',
];

/**
 * Only words every letter of which has sourced motion. If the alphabet ever
 * loses a letter, the drill shrinks rather than performing a guess.
 */
export function drillPool(available: ReadonlySet<string>): string[] {
  return WORD_POOL.filter((word) => [...word].every((letter) => available.has(letter)));
}

/**
 * Pick the next word, never repeating the one just shown. Injectable
 * randomness so tests are deterministic.
 */
export function nextWord(
  pool: readonly string[],
  previous: string,
  random: () => number = Math.random,
): string | null {
  const candidates = pool.filter((word) => word !== previous);
  if (!candidates.length) return pool[0] ?? null;
  return candidates[Math.min(candidates.length - 1, Math.floor(random() * candidates.length))]!;
}

export interface LetterMark {
  /** Null where the learner typed past the end of the word. */
  expected: string | null;
  /** What the learner typed at this position, or null if their answer was short. */
  got: string | null;
  ok: boolean;
}

/** Uppercase and strip everything that is not a letter, so "j o h n" reads as JOHN. */
export function normaliseAnswer(answer: string): string {
  return answer.toUpperCase().replace(/[^A-Z]/g, '');
}

/**
 * Per-position comparison against the target word. Runs to whichever is
 * longer: typing past the end of the word is a misread, not a clean read, so
 * those positions are marked too rather than going uninspected.
 */
export function markAnswer(word: string, answer: string): LetterMark[] {
  const typed = normaliseAnswer(answer);
  const length = Math.max(word.length, typed.length);
  return Array.from({ length }, (_, index) => {
    const expected = word[index] ?? null;
    const got = typed[index] ?? null;
    return { expected, got, ok: got === expected };
  });
}

/** A short settle after the last letter, so the hold registers before the ask. */
export const SETTLE_MS = 350;

/** How long the avatar has to spell the whole word: one step per letter. */
export function spellMs(word: string, speed: Speed): number {
  return word.length * stepMs(speed);
}
